import { useMemo } from 'react';
import {
  Box,
  Text,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useBinderCardColors } from '../../binder_report/hooks/useBinderCardColors';

interface MarketApprovalsCardProps {
  validations?: Validation[];
  remarks?: Remark[];
}

const MarketApprovalsCard = ({
  validations = [],
  remarks = [],
}: MarketApprovalsCardProps) => {
  const colors = useBinderCardColors();

  // Build market approval details by combining validations and remarks
  const marketApprovals = useMemo(() => {
    return validations.map((validation) => {
      const matchingRemark = remarks.find(
        (r) => r.validation_key === validation.validation_id,
      );

      return {
        validationName: validation.validation_name,
        validationId: validation.validation_id,
        remark: matchingRemark?.text || 'Remark approval required',
      };
    });
  }, [validations, remarks]);

  if (marketApprovals.length === 0) {
    return null;
  }

  return (
    <Box>
      <Heading size="sm" mb={4} color={colors.textColor}>
        Market Approvals
      </Heading>

      <Box
        borderWidth="1px"
        borderColor={colors.borderColor}
        borderRadius="md"
        bg={colors.bgColor}
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={colors.tableHeaderBg}>
              <Tr>
                <Th
                  width="200px"
                  borderColor={colors.borderColor}
                  py={3}
                  textTransform="none"
                  fontSize="sm"
                  fontWeight="bold"
                  color={colors.textColor}
                >
                  Validation
                </Th>
                <Th
                  borderColor={colors.borderColor}
                  py={3}
                  textTransform="none"
                  fontSize="sm"
                  fontWeight="bold"
                  color={colors.textColor}
                >
                  Details
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {marketApprovals.map((approval, index) => (
                <Tr key={`${approval.validationId}-${index}`}>
                  <Td
                    borderColor={colors.borderColor}
                    py={4}
                    verticalAlign="top"
                    textAlign="center"
                  >
                    <Text fontWeight="medium" color={colors.textColor}>
                      {approval.validationName}
                    </Text>
                    <Text fontSize="sm" color={colors.descriptionColor}>
                      ({approval.validationId})
                    </Text>
                  </Td>
                  <Td borderColor={colors.borderColor} py={4}>
                    <Text fontSize="sm" color={colors.textColor}>
                      {approval.remark}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default MarketApprovalsCard;
